<template>
  <v-dialog
    :value="value"
    max-width="640"
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @input="$emit('input', $event)"
  >
    <v-card rounded="lg" elevation="3">
      <v-card-title class="dialog-header">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-tag-outline</v-icon>
          <span class="text-h6 font-weight-medium">
            Return label
            <span v-if="info" class="grey--text text--darken-1 ml-1">{{ info.return.name }}</span>
          </span>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('input', false)" :disabled="working"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="dialog-content">
        <div v-if="loading" class="loading-container">
          <v-progress-circular indeterminate color="primary" size="40" />
          <div class="mt-3 grey--text">Checking which carriers can bring it back...</div>
        </div>

        <template v-else-if="info">
          <div class="text-caption grey--text mb-3">
            {{ info.return.order_name }} · {{ info.return.customer_name }}<span v-if="info.return.email"> · {{ info.return.email }}</span>
            · from {{ info.from_country }} · {{ items_summary }}
          </div>

          <!-- Existing labels -->
          <v-alert v-for="label in active_labels" :key="label.id" type="success" text dense class="mb-3">
            <div class="d-flex align-center flex-wrap">
              <div class="grow">
                <strong>{{ label.carrier_name || 'Label' }}</strong> · {{ label.tracking_number }}
                <span v-if="label.notified_customer"> · sent to the customer</span>
                <div class="text-caption">Created {{ label.created_at | DateFilter }} by {{ label.created_by_name }}</div>
              </div>
              <v-btn v-if="label.label_refs && label.label_refs.length" x-small rounded outlined class="ml-2" @click="open_file(label.label_refs.find(r => r.description === 'RETURN_LABEL') || label.label_refs[0])">
                <v-icon left x-small>mdi-file-pdf-box</v-icon>Label
              </v-btn>
              <v-btn v-if="label.qr_link" x-small rounded outlined class="ml-2" :href="label.qr_link" target="_blank" rel="noopener">
                <v-icon left x-small>mdi-qrcode</v-icon>QR
              </v-btn>
              <v-btn v-if="label.external_label_url" x-small rounded outlined class="ml-2" :href="label.external_label_url" target="_blank" rel="noopener">
                <v-icon left x-small>mdi-open-in-new</v-icon>Label
              </v-btn>
              <v-btn x-small rounded text color="error" class="ml-2" :loading="voiding === label.id" @click="void_label(label)">Void</v-btn>
            </div>
          </v-alert>
          <v-alert v-if="!active_labels.length && shopify_tracking" type="info" text dense class="mb-3">
            Shopify already has return tracking {{ shopify_tracking }} on this return. Creating a label here replaces it.
          </v-alert>

          <v-tabs v-model="tab" grow class="mb-3">
            <v-tab>Book a label</v-tab>
            <v-tab>Attach existing</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <!-- Book -->
            <v-tab-item>
              <v-alert v-if="!info.options.length" type="warning" text dense>
                None of your carrier accounts can carry a return from {{ info.from_country }} to the warehouse.
                <div v-for="line in info.unavailable" :key="line" class="text-caption mt-1">{{ line }}</div>
              </v-alert>
              <template v-else>
                <v-radio-group v-model="selected" hide-details class="mt-0">
                  <v-radio
                    v-for="option in info.options"
                    :key="`${option.carrier_account_id}:${option.service_id}`"
                    :value="`${option.carrier_account_id}:${option.service_id}`"
                    class="option-row"
                  >
                    <template v-slot:label>
                      <div>
                        <div class="font-weight-medium">{{ option.carrier_name }}</div>
                        <div class="text-caption grey--text">{{ option.description }}<span v-if="option.max_weight_in_grams"> · up to {{ option.max_weight_in_grams / 1000 }} kg</span></div>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
                <div v-for="line in info.unavailable" :key="line" class="text-caption grey--text mt-2">{{ line }}</div>
                <v-switch v-model="notify" hide-details class="mt-3" label="Email the label to the customer through Shopify" />
                <div class="text-caption grey--text mt-1">The merchant account pays for the return shipment. The label's tracking number is matched automatically when the parcel arrives.</div>
              </template>
            </v-tab-item>

            <!-- Attach -->
            <v-tab-item>
              <v-text-field v-model="external.tracking_number" label="Tracking number" dense outlined class="mt-2" />
              <v-text-field v-model="external.carrier_name" label="Carrier (optional)" dense outlined placeholder="Bring, UPS..." />
              <v-text-field v-model="external.tracking_url" label="Tracking link (optional)" dense outlined placeholder="https://" />
              <v-text-field v-model="external.label_url" label="Link to the label PDF (optional)" dense outlined placeholder="https://" hint="Shopify links to this file from the return and the customer email" persistent-hint />
              <v-switch v-model="notify" hide-details class="mt-3" label="Email the label to the customer through Shopify" />
            </v-tab-item>
          </v-tabs-items>
        </template>

        <v-alert v-if="error" type="error" dense text class="mt-3">{{ error }}</v-alert>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text rounded @click="$emit('input', false)" :disabled="working">Close</v-btn>
        <v-btn
          v-if="tab === 0"
          color="primary"
          rounded
          elevation="1"
          class="px-6"
          :disabled="!selected || working || loading"
          :loading="working"
          @click="create"
        >
          <v-icon left>mdi-tag-plus-outline</v-icon>
          {{ active_labels.length ? 'Create new label' : 'Create label' }}
        </v-btn>
        <v-btn
          v-else
          color="primary"
          rounded
          elevation="1"
          class="px-6"
          :disabled="!external.tracking_number || working || loading"
          :loading="working"
          @click="attach"
        >
          <v-icon left>mdi-link-variant</v-icon>
          Attach
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage'
import { DateFilter } from '@/utils/filters'

// Books or attaches a return label for an open Shopify return. The options come from the carriers
// that can carry a parcel from the customer's country back to the warehouse.
export default {
  filters: { DateFilter },
  props: {
    value: { type: Boolean, default: false },
    // { return_id, return_name, order_name }
    context: { type: Object, default: () => ({}) },
  },
  data(){
    return {
      loading: false,
      working: false,
      voiding: null,
      error: '',
      info: null,
      tab: 0,
      selected: null,
      notify: true,
      external: { tracking_number: '', carrier_name: '', tracking_url: '', label_url: '' },
    }
  },
  computed: {
    active_labels(){
      return (this.info?.labels || []).filter(l => !l.voided_at)
    },
    shopify_tracking(){
      return (this.info?.deliveries || []).map(d => d.tracking?.number).find(Boolean) || null
    },
    items_summary(){
      return (this.info?.return.items || []).map(i => `${i.quantity}x ${i.name}`).join(', ')
    },
  },
  watch: {
    value(open){
      if(open) this.load()
    },
  },
  methods: {
    async load(){
      this.info = null
      this.error = ''
      this.selected = null
      this.tab = 0
      this.external = { tracking_number: '', carrier_name: '', tracking_url: '', label_url: '' }
      if(!this.context.return_id) return
      this.loading = true
      try {
        this.info = await this.$store.dispatch('returns/get_label_options', { return_id: this.context.return_id })
        // Remember the last choice per country so repeat returns are one click
        const remembered = this.remembered_choice(this.info.from_country)
        const first = this.info.options.find(o => `${o.carrier_account_id}:${o.service_id}` === remembered) || this.info.options[0]
        this.selected = first ? `${first.carrier_account_id}:${first.service_id}` : null
        if(!this.info.options.length) this.tab = 1
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    remembered_choice(country){
      try { return localStorage.getItem(`return_label_choice:${country}`) } catch (e) { return null }
    },
    remember_choice(country, choice){
      try { localStorage.setItem(`return_label_choice:${country}`, choice) } catch (e) { /* storage unavailable */ }
    },
    async create(){
      const [carrier_account_id, service_id] = this.selected.split(':')
      this.working = true
      this.error = ''
      try {
        const label = await this.$store.dispatch('returns/create_label', { return_id: this.context.return_id, carrier_account_id, service_id, notify_customer: this.notify })
        this.remember_choice(this.info.from_country, this.selected)
        this.info.labels = [...this.info.labels, label]
        this.$store.commit('app/SET_SNACK_BAR', { text: `${label.carrier_name} return label ${label.tracking_number} created${label.notified_customer ? ' and sent to the customer' : ''}`, type: 'success' })
        this.$emit('created', label)
      } catch (e) {
        this.error = e.message
      } finally {
        this.working = false
      }
    },
    async attach(){
      this.working = true
      this.error = ''
      try {
        const label = await this.$store.dispatch('returns/attach_label', { return_id: this.context.return_id, ...this.external, notify_customer: this.notify })
        this.info.labels = [...this.info.labels, label]
        this.external = { tracking_number: '', carrier_name: '', tracking_url: '', label_url: '' }
        this.tab = 0
        this.$store.commit('app/SET_SNACK_BAR', { text: `Return label ${label.tracking_number} attached`, type: 'success' })
        this.$emit('created', label)
      } catch (e) {
        this.error = e.message
      } finally {
        this.working = false
      }
    },
    async void_label(label){
      const { ok } = await this.$prompt({
        text: `Void the return label ${label.tracking_number}? ${label.carrier_type === 'fedex' ? 'It is cancelled at FedEx.' : 'Unused labels are not charged.'} The label link stops working and the customer keeps the email, so create a new label if the return is still expected.`,
        buttons: [{ text: 'Void label', value: { ok: true } }, { text: 'Cancel', value: { ok: false } }],
      })
      if(!ok) return
      this.voiding = label.id
      try {
        const voided = await this.$store.dispatch('returns/void_label', { label_id: label.id })
        this.info.labels = this.info.labels.map(l => l.id === voided.id ? voided : l)
        this.$emit('voided', voided)
      } catch (e) {
        this.error = e.message
      } finally {
        this.voiding = null
      }
    },
    async open_file(label_ref){
      try {
        const url = await getDownloadURL(storageRef(getStorage(), label_ref.ref))
        window.open(url, '_blank', 'noopener')
      } catch (e) {
        this.error = e.message
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-header {
  padding: 16px 24px;
}

.dialog-content {
  padding: 20px 24px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.option-row {
  align-items: flex-start;
  margin-bottom: 12px;
}

@media (max-width: 600px) {
  .dialog-content {
    padding: 16px;
  }
}
</style>
